import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Server, AlertTriangle, CheckCircle2, X, Eye, Loader2, FolderOpen, Monitor } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function HostGroupsPage() {
  const [groups, setGroups] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupHosts, setGroupHosts] = useState(null);
  const [loadingHosts, setLoadingHosts] = useState(false);
  const { getAuthHeader } = useAuthStore();
  const navigate = useNavigate();

  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get(API + '/hostgroups', { headers: getAuthHeader() });
      setGroups(response.data.groups || []);
      setSummary(response.data.summary || {});
    } catch (err) {
      console.error('Error fetching groups:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchGroups();
    const interval = setInterval(fetchGroups, 60000);
    return () => clearInterval(interval);
  }, [fetchGroups]);

  const handleGroupClick = async (group) => {
    setSelectedGroup(group);
    setLoadingHosts(true);
    setGroupHosts(null);
    
    try {
      const response = await axios.get(API + '/hostgroups/' + group.id + '/hosts', { headers: getAuthHeader() });
      setGroupHosts(response.data);
    } catch (err) {
      console.error('Error fetching group hosts:', err);
    } finally {
      setLoadingHosts(false);
    }
  };

  const closeDialog = () => {
    setSelectedGroup(null);
    setGroupHosts(null);
  };

  const filteredGroups = groups.filter(g => g.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const getStatusColor = (status, hasHosts) => {
    if (!hasHosts || hasHosts === 0) return 'bg-gray-100 border-gray-200';
    if (status === 'warning') return 'bg-yellow-50 border-yellow-300 hover:bg-yellow-100';
    return 'bg-green-50 border-green-300 hover:bg-green-100';
  };

  const getStatusIcon = (status, hasHosts) => {
    if (!hasHosts || hasHosts === 0) return <Server className="h-8 w-8 text-gray-400" />;
    if (status === 'warning') return <AlertTriangle className="h-8 w-8 text-yellow-600" />;
    return <CheckCircle2 className="h-8 w-8 text-green-600" />;
  };

  return (
    <div className="space-y-6" data-testid="hostgroups-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Grupos de Hosts</h2>
          <p className="text-muted-foreground">Visualize o status de todos os grupos do Zabbix</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="secondary" className="bg-green-100 text-green-700 px-3 py-1">
            <CheckCircle2 className="h-3 w-3 mr-1" />{summary.groups_ok || 0} OK
          </Badge>
          <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 px-3 py-1">
            <AlertTriangle className="h-3 w-3 mr-1" />{summary.groups_with_problems || 0} Com Problemas
          </Badge>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar grupo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Badge variant="outline">{filteredGroups.length} grupos</Badge>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1,2,3,4,5,6,7,8].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredGroups.map(group => (
            <Card
              key={group.id}
              className={`cursor-pointer transition-all duration-200 border-2 ${getStatusColor(group.status, group.total_hosts)}`}
              onClick={() => handleGroupClick(group)}
              data-testid={`hostgroup-card-${group.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm truncate" title={group.name}>{group.name}</h3>
                    <div className="mt-3 space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Total de Hosts:</span>
                        <span className="font-medium">{group.total_hosts}</span>
                      </div>
                      {group.total_hosts > 0 && (
                        <>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-green-600">OK:</span>
                            <span className="font-medium text-green-600">{group.hosts_ok}</span>
                          </div>
                          {group.hosts_with_problems > 0 && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-yellow-600">Com Problemas:</span>
                              <span className="font-medium text-yellow-600">{group.hosts_with_problems}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="ml-3">
                    {getStatusIcon(group.status, group.total_hosts)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {filteredGroups.length === 0 && (
            <div className="col-span-full text-center py-12">
              <FolderOpen className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nenhum grupo encontrado</p>
            </div>
          )}
        </div>
      )}

      <Dialog open={selectedGroup !== null} onOpenChange={closeDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${selectedGroup?.status === 'warning' ? 'bg-yellow-100' : 'bg-green-100'}`}>
                {selectedGroup?.status === 'warning' ? (
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                ) : (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                )}
              </div>
              <div>
                <span className="text-xl">{selectedGroup?.name}</span>
                <p className="text-sm text-muted-foreground font-normal">
                  {selectedGroup?.total_hosts} hosts no grupo
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>

          {loadingHosts ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : groupHosts && (
            <div className="flex-1 overflow-hidden">
              <div className="flex items-center gap-3 mb-4">
                <Badge variant="secondary" className="bg-green-100 text-green-700">
                  <CheckCircle2 className="h-3 w-3 mr-1" />{groupHosts.summary?.up || 0} UP
                </Badge>
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">
                  <AlertTriangle className="h-3 w-3 mr-1" />{groupHosts.summary?.warning || 0} Warning
                </Badge>
                <Badge variant="secondary" className="bg-red-100 text-red-700">
                  <AlertTriangle className="h-3 w-3 mr-1" />{groupHosts.summary?.down || 0} Down
                </Badge>
              </div>

              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Host</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>Problema Atual</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupHosts.hosts?.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          <Monitor className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                          Nenhum host neste grupo
                        </TableCell>
                      </TableRow>
                    )}
                    {groupHosts.hosts?.map(host => (
                      <TableRow key={host.id} className="hover:bg-muted/50">
                        <TableCell>
                          {host.status === 'up' && (
                            <Badge className="bg-green-100 text-green-700">UP</Badge>
                          )}
                          {host.status === 'warning' && (
                            <Badge className="bg-yellow-100 text-yellow-700">WARNING</Badge>
                          )}
                          {host.status === 'down' && (
                            <Badge className="bg-red-100 text-red-700 animate-pulse">DOWN</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{host.name}</p>
                            {host.host !== host.name && (
                              <p className="text-xs text-muted-foreground">{host.host}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{host.ip || '-'}</TableCell>
                        <TableCell className="max-w-xs">
                          {host.current_problem ? (
                            <span className="text-sm text-yellow-700 truncate block" title={host.current_problem}>
                              {host.current_problem}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              closeDialog();
                              navigate('/dashboard/hosts/' + host.id);
                            }}
                            title="Ver detalhes"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
